import { useViewport } from "@bookingcom/bui-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { useSelector } from "react-redux";
import { getSearchCriteria } from "../../../store/searchCriteria/selectors.js";
import { getSearchDisplayOptions } from "../../../store/searchResults/selectors.js";
import { getFareOffersWithDetails, getIsBrandedFaresEmpty, getIsBrandedFaresLoading } from "../../../store/brandedFareOffers/selectors.js";
import flights_apex_web_ancillaries_micro_conversion_aa from "../../../utils/experiments/apex/flights_apex_web_ancillaries_micro_conversion_aa.js";
import { useCardSegmentsCondensedController } from "./components/elements/CardSegmentCondensed/useCardSegmentsCondensed.controller.js";
import useFetchBrandedFares from "../../../branded-fares/hooks/useFetchBrandedFares.js";
import useIsInViewport from "../../../hooks/useIsInViewport.js";
import { carrierUtils } from "@flights/web-api-utils-universal";
import useGlobalContext from "../../../hooks/useGlobalContext.js";
import { getCampaignBestOfferHighlightConfig } from "../../../utils/flightCampaignConfig.js";
import useLocaleContext from "../../../hooks/useLocaleContext.js";
import { FlightCardHighlightStyle } from "./components/FlightCardHighlight.js";
import useGetSearchResultsBound from "../../../hooks/useGetSearchResultsBound.js";
import { isDeltaAirlinesOffer, shouldPreopenBrandedFares } from "./utils.js";
import flights_apex_web_delta_airlines_pilot_2026 from "../../../utils/experiments/apex/flights_apex_web_delta_airlines_pilot_2026.js";
import flights_web_delta_airlines_pilot_phase_2 from "../../../utils/experiments/apex/flights_web_delta_airlines_pilot_phase_2.js";
import flights_apex_branded_fares_loading_sr from "../../../utils/experiments/apex/flights_apex_branded_fares_loading_sr.js";
import flights_apex_tabular_branded_fares_sr from "../../../utils/experiments/apex/flights_apex_tabular_branded_fares_sr.js";
import { isFlightDealsLandingHighlightedOffer } from "../../../utils/flight-deals.js";
const useFlightCardDesktopController = (flight) => {
    const i18n = useI18n();
    const { isInternal } = useGlobalContext();
    const bound = useGetSearchResultsBound();
    const [breakdownOpen, setBreakdownOpen] = useState(false);
    const flightCardBoundRef = useRef(null);
    const isInViewport = useIsInViewport(flightCardBoundRef.current);
    const { isRTL } = useLocaleContext();
    const { adults, children } = useSelector(getSearchCriteria);
    const displayOptions = useSelector(getSearchDisplayOptions);
    const isBrandedFaresLoading = useSelector((state) => getIsBrandedFaresLoading(state, flight.token));
    const isBrandedFaresEmpty = useSelector((state) => getIsBrandedFaresEmpty(state, flight.token));
    const fareOffersWithDetails = useSelector(getFareOffersWithDetails);
    const brandedFareOffers = fareOffersWithDetails[flight.token]?.brandedFareOffers;
    const isDelta = isDeltaAirlinesOffer(flight);
    const shouldPreopenBrandedFaresForFlight = useMemo(() => shouldPreopenBrandedFares(flight), [flight]);
    const hasPrefetchedBrandedFareOffers = !!flight.brandedFareOffers && flight.brandedFareOffers?.length > 0;
    const canShowFareSelector = flight.requestableBrandedFares || hasPrefetchedBrandedFareOffers;
    if (canShowFareSelector) {
        flights_apex_branded_fares_loading_sr.trackStages({ isMobile: false });
        flights_apex_tabular_branded_fares_sr.trackStages();
    }
    const isBrandedFaresLoadingExperimentEnabled = canShowFareSelector && !!flights_apex_branded_fares_loading_sr.trackWithDefaultStage();
    const isTabularBrandedFaresExperimentEnabled = canShowFareSelector && !!flights_apex_tabular_branded_fares_sr.trackWithDefaultStage();
    const isInBrandedFaresExperimentVariant = isBrandedFaresLoadingExperimentEnabled || isTabularBrandedFaresExperimentEnabled;
    const showFareSelectorDefault = canShowFareSelector &&
        (displayOptions?.brandedFaresShownByDefault ||
            shouldPreopenBrandedFaresForFlight ||
            bound === "outbound" ||
            bound === "inbound");
    const [showFareSelector, setShowFareSelector] = useState(showFareSelectorDefault);
    const [isWaitingForData, setIsWaitingForData] = useState(false);
    useEffect(() => {
        if (showFareSelectorDefault) {
            flights_apex_tabular_branded_fares_sr.stages.users_click_on_expanded_bf_button();
        }
    }, [showFareSelectorDefault]);
    const isFirefly = flight.token.startsWith("c8e49_");
    flights_apex_branded_fares_loading_sr.trackPreExpanded({
        isDelta,
        isFirefly,
        isPreExpanded: showFareSelectorDefault
    });
    if (isDelta && showFareSelectorDefault) {
        flights_apex_branded_fares_loading_sr.stages.delta_pre_expanded();
    }
    if (isFirefly && showFareSelectorDefault) {
        flights_apex_branded_fares_loading_sr.stages.firefly_pre_expanded();
    }
    const toggleShowFareSelector = useCallback(() => {
        flights_apex_branded_fares_loading_sr.trackButtonClick({
            isBrandedFaresEmpty,
            isBrandedFaresLoading,
            showFareSelector,
            brandedFareOffersCount: brandedFareOffers?.length || 0
        });
        flights_apex_tabular_branded_fares_sr.trackButtonClick({
            isBrandedFaresLoading,
            showFareSelector,
            brandedFareOffersCount: brandedFareOffers?.length || 0
        });
        if (isBrandedFaresLoadingExperimentEnabled && !showFareSelector && isBrandedFaresLoading) {
            setIsWaitingForData(true);
            return;
        }
        if (showFareSelector) {
            setIsWaitingForData(false);
        }
        if (!showFareSelector) {
            flights_apex_web_ancillaries_micro_conversion_aa.goals.show_fare_options_sr();
        }
        setShowFareSelector(!showFareSelector);
    }, [
        showFareSelector,
        isBrandedFaresLoading,
        brandedFareOffers,
        isBrandedFaresEmpty,
        isBrandedFaresLoadingExperimentEnabled
    ]);
    const { isSmall } = useViewport();
    const { segmentsData, carrierText } = useCardSegmentsCondensedController(flight.segments);
    const travellersCount = adults + children.length;
    const carriers = carrierUtils.getUniqueMarketingCarrierCodes(flight.segments);
    const highlightFireflySR = isInternal &&
        flight.token.startsWith("c8e49_") &&
        new URLSearchParams(window.location.search).get("highlight_firefly") === "1";
    const isCrossSellOffer = flight.highlightInfo?.highlight && flight.highlightInfo.reason === "CROSS_SELL_OFFER";
    const isGeniusHighlightedOffer = flight.highlightInfo?.highlight && flight.highlightInfo.reason === "GENIUS_AIRLINE_HIGHLIGHTED_OFFER";
    const isGeniusUprankedOffer = flight.highlightInfo?.highlight &&
        (flight.highlightInfo.reason === "GENIUS_AIRLINE_FARE_OFFER" ||
            flight.highlightInfo.reason === "GENIUS_AIRLINE_UPRANKED_OFFER");
    const isAitpOffer = flight.highlightInfo?.highlight && flight.highlightInfo.reason === "AITP_OFFER";
    const isDealsLandingHighlightedOffer = isFlightDealsLandingHighlightedOffer(flight);
    const shouldPinOfferOnSr = isCrossSellOffer ||
        isGeniusHighlightedOffer ||
        isGeniusUprankedOffer ||
        isAitpOffer ||
        isDealsLandingHighlightedOffer;
    const getPinnedHighlightText = () => {
        if (isGeniusHighlightedOffer || isGeniusUprankedOffer)
            return flight.highlightInfo?.message ?? "";
        return flight.highlightInfo?.message || i18n.trans(t("flights_meta_sr_pinned_flight_label"));
    };
    const pinnedHighlightText = getPinnedHighlightText();
    const pinnedInfoTooltip = isGeniusUprankedOffer && flight.highlightInfo?.infoMessage
        ? {
            content: flight.highlightInfo.infoMessage.subtitle,
            ariaLabel: i18n.trans(t("a11y_flights_sr_featured_flight_more_informatio_popup")),
            title: flight.highlightInfo.infoMessage.title
        }
        : undefined;
    const pinnedHighlightStyle = isGeniusHighlightedOffer
        ? FlightCardHighlightStyle.NEUTRAL
        : FlightCardHighlightStyle.PRIMARY;
    const { bestOfferHighlightStyle, bestOfferHighlightText } = getCampaignBestOfferHighlightConfig(flight.campaignDisplay?.bestOfferHighlight);
    useFetchBrandedFares(flight.token, isInViewport || shouldPreopenBrandedFaresForFlight, !!flight.requestableBrandedFares, true, !!flight.brandedFareInfo?.fareName, carriers);
    useEffect(() => {
        if (isBrandedFaresLoadingExperimentEnabled && displayOptions?.brandedFaresShownByDefault && isBrandedFaresEmpty) {
            setShowFareSelector(false);
        }
    }, [isBrandedFaresLoadingExperimentEnabled, isBrandedFaresEmpty, displayOptions?.brandedFaresShownByDefault]);
    useEffect(() => {
        if (isInBrandedFaresExperimentVariant && isWaitingForData && !isBrandedFaresLoading) {
            if (!isBrandedFaresEmpty) {
                setShowFareSelector(true);
            }
            setIsWaitingForData(false);
        }
    }, [isInBrandedFaresExperimentVariant, isWaitingForData, isBrandedFaresLoading, isBrandedFaresEmpty]);
    useEffect(() => {
        if (isInViewport && isDelta) {
            flights_apex_web_delta_airlines_pilot_2026.goals.delta_offer_seen();
            flights_web_delta_airlines_pilot_phase_2.goals.delta_offer_seen();
        }
    }, [isInViewport, isDelta]);
    useEffect(() => {
        if (showFareSelector && brandedFareOffers && brandedFareOffers?.length > 3) {
            flights_apex_tabular_branded_fares_sr.stages.users_more_than_3_bfs();
        }
    }, [showFareSelector, brandedFareOffers]);
    return {
        breakdownOpen,
        setBreakdownOpen,
        travellersCount,
        canShowFareSelector,
        toggleShowFareSelector,
        showFareSelector,
        isBrandedFaresLoading,
        isBrandedFaresEmpty,
        isWaitingForData,
        isBrandedFaresLoadingExperimentEnabled,
        isTabularBrandedFaresExperimentEnabled,
        isInBrandedFaresExperimentVariant,
        isSmall,
        segmentsData,
        carrierText,
        highlightFireflySR,
        shouldPinOfferOnSr,
        bestOfferHighlightStyle,
        bestOfferHighlightText,
        isRTL,
        flightCardBoundRef,
        isOutboundFlight: bound === "outbound",
        isInboundFlight: bound === "inbound",
        showPricePerPerson: bound === "inbound" || travellersCount > 1,
        priceLabel: i18n.trans(travellersCount > 1 ? t("flights_sr_rt_price_per") : t("flights_sr_price_round_trip")),
        priceAriaLabel: i18n.trans(t("flights_a11y_price_breakdown_close_sr")),
        fareOptionsCta: i18n.trans(t("flights_sr_cta_fares")),
        viewDetailsCta: bound === "outbound"
            ? i18n.trans(t("flights_sr_split_bounds_us_cta_select"))
            : i18n.trans(t("flights_sr_cta_details")),
        flightPinnedLabel: pinnedHighlightText,
        isGeniusHighlightedOffer,
        isGeniusUprankedOffer,
        pinnedInfoTooltip,
        pinnedHighlightStyle,
        hasGeniusAirlineFundedDiscount: flight.priceBreakdown?.bcomPricingItems?.some((item) => item.itemType === "GENIUS_AIRLINE_FARE")
    };
};
export default useFlightCardDesktopController;
